import { useState, useEffect, useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';

/**
 * Custom hook for managing Report Currency filters
 * This hook filters data client-side since Report Currencies are loaded as part of the invoice detail
 * @param {Array} reportCurrencies - Array of report currency objects
 * @param {string} persistenceKey - Key for localStorage persistence
 * @returns {Object} - Filter state, methods, and filtered data
 */
function useReportCurrencyFilters(
  reportCurrencies = [],
  persistenceKey = null
) {
  // Configuration for Report Currency filters
  const columnConfig = [
    {
      id: 'id',
      label: 'ID',
      filterType: 'text',
      apiParam: 'id',
    },
    {
      id: 'statement_date',
      label: 'Statement Date',
      filterType: 'month',
      apiParam: 'statement_date',
    },
  ];

  // Load persisted filters if available
  const loadPersistedFilters = useCallback(() => {
    if (!persistenceKey || typeof window === 'undefined') return {};
    try {
      const persisted = localStorage.getItem(`filters_${persistenceKey}`);
      return persisted ? JSON.parse(persisted) : {};
    } catch (err) {
      return {};
    }
  }, [persistenceKey]);

  const [filters, setFilters] = useState(() => {
    const persisted = loadPersistedFilters();
    return persisted || {};
  });

  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const [tempFilters, setTempFilters] = useState({});

  // Save filters to localStorage when they change
  useEffect(() => {
    if (!persistenceKey || typeof window === 'undefined') return;
    try {
      if (Object.keys(filters).length === 0) {
        localStorage.removeItem(`filters_${persistenceKey}`);
      } else {
        localStorage.setItem(
          `filters_${persistenceKey}`,
          JSON.stringify(filters)
        );
      }
    } catch (err) {
      // silent error
    }
  }, [filters, persistenceKey]);

  // Convert filters object to activeFilters array
  const activeFilters = useMemo(() => {
    return Object.entries(filters)
      .map(([columnId, filterValues]) => {
        const column = columnConfig.find(col => col.id === columnId);
        if (!column) return null;

        // Format display value based on filter type
        let displayValue = filterValues.value;
        if (column.filterType === 'month' && filterValues.value) {
          // Format month display as "Jan 2024"
          const date = DateTime.fromISO(`${filterValues.value}-01`);
          displayValue = date.toFormat('MMM yyyy');
        }

        return {
          id: columnId,
          columnId,
          label: column.label,
          value: displayValue,
          filterType: column.filterType,
          operator: filterValues.operator,
          ...filterValues,
        };
      })
      .filter(Boolean);
  }, [filters]);

  // Filter the report currencies data based on active filters
  const filteredData = useMemo(() => {
    if (!reportCurrencies || reportCurrencies.length === 0) {
      return [];
    }

    return reportCurrencies.filter(item => {
      return Object.entries(filters).every(([columnId, filterValues]) => {
        const column = columnConfig.find(col => col.id === columnId);
        if (!column || !filterValues.value) return true;

        switch (column.filterType) {
          case 'text': {
            // For ID filter, convert to string and use case-insensitive includes
            const itemValue = String(item[columnId] || '');
            const searchValue = String(filterValues.value || '');
            return itemValue.toLowerCase().includes(searchValue.toLowerCase());
          }

          case 'month': {
            // For month filter, extract year-month from statement_date
            if (!item.statement_date) return false;
            const itemDate = DateTime.fromISO(item.statement_date);
            const filterYearMonth = filterValues.value; // Format: "2024-01"
            const itemYearMonth = itemDate.toFormat('yyyy-MM');
            return itemYearMonth === filterYearMonth;
          }

          default:
            return true;
        }
      });
    });
  }, [reportCurrencies, filters]);

  // Get unique months from the data for month filter options
  const availableMonths = useMemo(() => {
    if (!reportCurrencies || reportCurrencies.length === 0) {
      return [];
    }

    const months = new Set();
    reportCurrencies.forEach(item => {
      if (item.statement_date) {
        const date = DateTime.fromISO(item.statement_date);
        const yearMonth = date.toFormat('yyyy-MM');
        months.add(yearMonth);
      }
    });

    return Array.from(months)
      .sort()
      .reverse() // Most recent first
      .map(yearMonth => {
        const date = DateTime.fromISO(`${yearMonth}-01`);
        return {
          value: yearMonth,
          label: date.toFormat('MMM yyyy'),
        };
      });
  }, [reportCurrencies]);

  // Open filter popup
  const openFilterPopup = useCallback(
    (columnId, filterValues = {}) => {
      setOpenFilterColumn({ id: columnId });
      // Load existing filter values if any
      const existingFilter = filters[columnId];
      setTempFilters(existingFilter || filterValues);
    },
    [filters]
  );

  // Update temp filter values
  const updateTempFilter = useCallback((key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Remove filter by column
  const removeFilter = useCallback(columnId => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  }, []);

  // Clear current filter
  const clearCurrentFilter = useCallback(() => {
    if (openFilterColumn?.id) {
      removeFilter(openFilterColumn.id);
    }
    setOpenFilterColumn(null);
    setTempFilters({});
  }, [openFilterColumn, removeFilter]);

  // Apply filter
  const applyFilter = useCallback(
    (columnId, filterValues) => {
      // If called without parameters, use current openFilterColumn and tempFilters
      if (!columnId && openFilterColumn?.id) {
        const targetColumnId = openFilterColumn.id;
        const targetValues = tempFilters;

        // Check if there are valid values to apply the filter
        const hasValidValues = Object.keys(targetValues).some(key => {
          return targetValues[key] !== undefined && targetValues[key] !== '';
        });

        if (hasValidValues) {
          setFilters(prev => ({ ...prev, [targetColumnId]: targetValues }));
        }
        setOpenFilterColumn(null);
        setTempFilters({});
      } else if (columnId) {
        // Called with parameters
        setFilters(prev => ({ ...prev, [columnId]: filterValues }));
        setOpenFilterColumn(null);
        setTempFilters({});
      }
    },
    [openFilterColumn, tempFilters]
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  // Check if column has filter
  const hasColumnFilter = useCallback(
    columnId => {
      return filters[columnId] && Object.keys(filters[columnId]).length > 0;
    },
    [filters]
  );

  return {
    filters,
    activeFilters,
    filteredData,
    availableMonths,
    columnConfig,
    openFilterColumn,
    tempFilters,
    openFilterPopup,
    updateTempFilter,
    applyFilter,
    removeFilter,
    clearCurrentFilter,
    clearAllFilters,
    hasColumnFilter,
  };
}

export default useReportCurrencyFilters;
